import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import fullLogoBlue from "../assets/img/brand/full-logo-blue.svg";

interface SplashScreenProps {
  visible: boolean;
}

const FADE_OUT_MS = 500;

export function SplashScreen({ visible }: SplashScreenProps) {
  const [mounted, setMounted] = useState(visible);
  const [show, setShow] = useState(visible);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      const frame = window.requestAnimationFrame(() => {
        setShow(true);
      });
      return () => window.cancelAnimationFrame(frame);
    }

    setShow(false);
    const timer = window.setTimeout(() => {
      setMounted(false);
    }, FADE_OUT_MS);

    return () => window.clearTimeout(timer);
  }, [visible]);

  if (!mounted) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#ffffff",
        zIndex: 9999,
        opacity: show ? 1 : 0,
        transition: `opacity ${FADE_OUT_MS}ms ease-out`,
        overflow: "hidden",
        "@keyframes subtlePulse": {
          "0%, 100%": {
            opacity: 1,
            transform: "scale(1)",
          },
          "50%": {
            opacity: 0.9,
            transform: "scale(1.01)",
          },
        },
        "@keyframes wave": {
          "0%": {
            transform: "translateX(0)",
          },
          "100%": {
            transform: "translateX(-50%)",
          },
        },
        "& img": {
          animation: "subtlePulse 3s ease-in-out infinite",
          maxWidth: "200px",
          width: "100%",
          height: "auto",
          zIndex: 1,
        },
      }}
    >
      <img src={fullLogoBlue} alt="Sailmate" />
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          height: "120px",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          sx={{
            width: "200%",
            height: "100%",
            animation: "wave 10s linear infinite",
          }}
        >
          <path
            d="M0,60 Q300,20 600,60 T1200,60 L1200,120 L0,120 Z"
            fill="#123860"
            opacity="0.25"
          />
        </Box>
        <Box
          component="svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          sx={{
            width: "200%",
            height: "100%",
            animation: "wave 8s linear infinite",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <path
            d="M0,80 Q300,40 600,80 T1200,80 L1200,120 L0,120 Z"
            fill="#123860"
            opacity="0.5"
          />
        </Box>
        <Box
          component="svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          sx={{
            width: "200%",
            height: "100%",
            animation: "wave 12s linear infinite",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <path
            d="M0,70 Q300,30 600,70 T1200,70 L1200,120 L0,120 Z"
            fill="#123860"
          />
        </Box>
      </Box>
    </Box>
  );
}
